import {
  GetObjectCommand,
  NoSuchKey,
  PutObjectCommand,
  S3Client,
} from "@aws-sdk/client-s3";
import type { ServiceMap } from "../../../types";
import { getCloudConfig } from "./cloudConfig";
import { cloudAuthSession } from "./cloudAuthSession";

const emptyMap = (): ServiceMap => ({ nodes: [], edges: [] });

const isNoSuchKeyError = (error: unknown): boolean => {
  return (
    error instanceof NoSuchKey ||
    (error instanceof Error && error.name === "NoSuchKey")
  );
};

const getServiceMapObjectKey = (dashboardId: string): string => {
  return `dashboards/${dashboardId}/service-map.json`;
};

const getLegacyServiceMapObjectKeys = (dashboardId: string): string[] => [
  `${dashboardId}_system_map/service-map.json`,
  `/${dashboardId}.json`,
];

const parseServiceMap = (raw: string): ServiceMap => {
  if (!raw.trim()) {
    return emptyMap();
  }

  const parsed = JSON.parse(raw) as unknown;
  if (
    typeof parsed === "object" &&
    parsed !== null &&
    "nodes" in parsed &&
    "edges" in parsed
  ) {
    return parsed as ServiceMap;
  }

  return emptyMap();
};

const createS3Client = (): S3Client => {
  const { identityPoolRegion } = getCloudConfig();
  return new S3Client({
    region: identityPoolRegion,
    credentials: async () => cloudAuthSession.getAwsCredentials(),
    requestHandler: {
      cache: "no-store",
    },
  });
};

export const serviceMapS3Store = {
  get: async (dashboardId: string): Promise<ServiceMap> => {
    const { s3BucketName } = getCloudConfig();
    const key = getServiceMapObjectKey(dashboardId);
    const s3 = createS3Client();

    try {
      const response = await s3.send(
        new GetObjectCommand({
          Bucket: s3BucketName,
          Key: key,
        }),
      );

      const body = await response.Body?.transformToString();
      return parseServiceMap(body ?? "");
    } catch (error) {
      if (!isNoSuchKeyError(error)) {
        throw error;
      }

      for (const legacyKey of getLegacyServiceMapObjectKeys(dashboardId)) {
        try {
          const legacyResponse = await s3.send(
            new GetObjectCommand({
              Bucket: s3BucketName,
              Key: legacyKey,
            }),
          );

          const legacyBody = await legacyResponse.Body?.transformToString();
          return parseServiceMap(legacyBody ?? "");
        } catch (legacyError) {
          if (!isNoSuchKeyError(legacyError)) {
            throw legacyError;
          }
        }
      }

      return emptyMap();
    }
  },

  put: async (dashboardId: string, map: ServiceMap): Promise<void> => {
    const { s3BucketName } = getCloudConfig();
    const key = getServiceMapObjectKey(dashboardId);
    const s3 = createS3Client();

    await s3.send(
      new PutObjectCommand({
        Bucket: s3BucketName,
        Key: key,
        ContentType: "application/json",
        CacheControl: "no-store, no-cache, max-age=0, must-revalidate",
        Body: JSON.stringify(map),
      }),
    );
  },
};
